import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { GameCard } from '@/components/ui/GameCard';
import { AppHeader } from '@/components/ui/AppHeader';
import { GamesHeader } from '@/components/ui/GamesHeader';
import { apiFetch, ApiError } from '@/lib/api';
import type { Game } from '@/lib/db/schema';

export function Games() {
  const { user } = useAuth();
  const [games, setGames] = useState<Game[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [profile, setProfile] = useState<{ displayName?: string; isAdmin?: boolean } | null>(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [gamesRes, profileRes] = await Promise.all([
        apiFetch('/api/games'),
        apiFetch('/api/profile'),
      ]);
      const [gamesData, profileData] = await Promise.all([
        gamesRes.json(),
        profileRes.json(),
      ]);
      setGames(gamesData.games || []);
      setProfile(profileData.profile);
    } catch (err) {
      const message = err instanceof ApiError
        ? err.message
        : 'Failed to load data. Please try again.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-neutral-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-lg font-medium text-white mb-2">Something went wrong</h2>
          <p className="text-neutral-400 text-sm mb-6">{error}</p>
          <button
            onClick={fetchData}
            className="inline-flex items-center gap-2 px-4 py-2 bg-neutral-800 text-white rounded-lg text-sm hover:bg-neutral-700 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <AppHeader displayName={profile?.displayName} isAdmin={profile?.isAdmin} />

      {/* Games Content */}
      <main className="max-w-5xl mx-auto px-4 py-8">
        <GamesHeader />

        {games.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-neutral-900 rounded-full flex items-center justify-center mx-auto mb-4">
              <Plus className="w-8 h-8 text-neutral-600" />
            </div>
            <h2 className="text-lg font-medium text-white mb-2">No campaigns yet</h2>
            <p className="text-neutral-500 text-sm mb-6">
              Create your first game to begin your journey into Vannar.
            </p>
            <Link
              to="/new-game"
              className="inline-block px-6 py-2.5 bg-white text-black rounded-lg text-sm font-medium hover:bg-neutral-200 transition-colors"
            >
              Create your first game
            </Link>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {games.map((game) => (
              <GameCard key={game.id} game={game} isOwner={game.ownerId === user?.id} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
